import React from 'react'
import styled from 'styled-components'
import get from 'lodash/get'

import { useGetPhotosForListingQuery } from '../../../hooks/useQueries'

import HeartOnIcon from '../../icons/BucketlistHeartOn'
import HeartOffIcon from '../../icons/BucketlistHeartOff'
import { normalizeKeys } from '../../utils/propsUtils'

import CardSlider from '../shared/CardSlider'
import ListingDetails from '../shared/ListingDetails'
import DiscountBanner from '../shared/DiscountBanner'
import AvailableDates from '../shared/AvailableDates'
import { MediaWrapperOrientated } from '../shared/MediaWrapper'
import { CardWrapper, Content, BucketListButton } from '../shared/styled'

import { CamelCaseListing, SnakeCaseListing } from '../types'

export const ListingContent = styled(Content)`
  white-space: break-spaces;
`

type ListingCardProps = {
  listing: CamelCaseListing | SnakeCaseListing
  isInEscape?: boolean
  asLink?: boolean
  noBucketList?: boolean
  noGraphQL?: boolean
  onSelectEscapeClick?: () => any
  bucketListButtonLeft?: boolean
  compactCard?: boolean
  /** Optionally pass in the photo to show as first/cover on the card */
  coverPhoto?: Pick<GQL_PhotoImage, 'medium' | 'large'>
  availableDates?: { startDate: string; endDate: string; total: number }[]
}

const ListingCard = ({
  listing,
  asLink,
  noBucketList = false,
  isInEscape = false,
  onSelectEscapeClick = undefined,
  bucketListButtonLeft = false,
  noGraphQL = false,
  compactCard,
  coverPhoto,
  availableDates,
}: ListingCardProps) => {
  const {
    id,
    url,
    title,
    price,
    pricingSuffix,
    nicePricingSuffix,
    location,
    city,
    capacity,
    reviewScore,
    pricing,
    discount,
    photos,
    instantBookEnabled,
    subcategory,
  } = normalizeKeys(listing) as CamelCaseListing

  const { data: photoData } = useGetPhotosForListingQuery({
    variables: { id: id?.toString() },
    skip: noGraphQL,
    context: { batching: true },
  })

  const sliderPhotos = [
    ...(coverPhoto ? [{ image: coverPhoto }] : []),
    ...(get(photoData, 'photos', photos) || []),
  ]

  return (
    <CardWrapper>
      <MediaWrapperOrientated>
        {photoData && (
          <CardSlider
            title={title}
            photos={sliderPhotos}
            url={url}
            asLink={asLink}
          />
        )}

        {!noBucketList && onSelectEscapeClick && (
          <BucketListButton
            onClick={onSelectEscapeClick}
            isActive={isInEscape}
            left={bucketListButtonLeft}
          >
            <HeartOnIcon />
            <HeartOffIcon />
          </BucketListButton>
        )}
      </MediaWrapperOrientated>

      <DiscountBanner discount={discount} />

      <ListingContent compact={compactCard}>
        <ListingDetails
          title={title}
          capacity={capacity}
          locationName={location?.name}
          reviewScore={reviewScore}
          subcategory={subcategory}
          city={city}
          price={price}
          pricing={pricing}
          pricingSuffix={pricingSuffix}
          nicePricingSuffix={nicePricingSuffix}
          instantBookEnabled={instantBookEnabled}
          compact={compactCard}
          linkOptions={{
            url,
            asLink,
          }}
        />
      </ListingContent>

      {availableDates?.length > 0 && (
        <div style={{ marginTop: '-24px', marginBottom: '32px' }}>
          <AvailableDates
            availableDates={availableDates}
            linkOptions={{ url, asLink }}
            thumbnail={
              coverPhoto?.medium || photoData?.photos?.[0]?.image?.medium
            }
            listingTitle={title}
            capacity={capacity}
            locationName={location?.name}
          />
        </div>
      )}
    </CardWrapper>
  )
}

export default ListingCard
